import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEye, faPencil, faTrash, faKey, faToggleOn, faToggleOff,
  faUsers, faTrashArrowUp,
} from '@fortawesome/free-solid-svg-icons';
import { customerService, type Customer } from '@/services/customer.service';
import { useHasPermission } from '@/hooks/use-permissions';
import { SkeletonPage } from '@/components/skeletons';
import { SearchBar } from '@/components/shared/SearchBar';
import { EmptyState } from '@/components/shared/EmptyState';
import { AccessRestricted } from '@/components/shared/AccessRestricted';
import { isForbiddenError } from '@/lib/is-forbidden-error';
import { Pagination } from '@/components/shared/Pagination';
import { Button } from '@/components/ui/button';
import { toast, confirmDelete, swalConfirm } from '@/lib/swal';
import { useDebounce } from '@/hooks/use-debounce';
import { ROUTES } from '@/routes/routes';
import { CustomerFormModal } from './components/CustomerFormModal';

const PAGE_SIZE = 20;

function IconBtn({ icon, label, onClick, danger }: { icon: typeof faEye; label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground ${danger ? 'hover:bg-destructive/10 hover:text-destructive' : ''}`}
    >
      <FontAwesomeIcon icon={icon} className="h-3.5 w-3.5" />
    </button>
  );
}

export function CustomersPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const can = useHasPermission();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Customer | null>(null);

  const canCreate = can('customer:create');
  const canEdit = can('customer:update');
  const canDelete = can('customer:delete');
  const canReset = can('customer:reset-password');

  const { data, isLoading, error } = useQuery({
    queryKey: ['customers', page, debouncedSearch],
    queryFn: () => customerService.list({ page, pageSize: PAGE_SIZE, search: debouncedSearch || undefined }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['customers'] });

  const deleteM = useMutation({
    mutationFn: customerService.remove,
    onSuccess: () => { toast.success('Customer deleted'); invalidate(); },
    onError: () => toast.error('Failed to delete customer'),
  });
  const toggleM = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => customerService.update(id, { isActive }),
    onSuccess: () => { toast.success('Status updated'); invalidate(); },
    onError: () => toast.error('Failed to update status'),
  });
  const resetM = useMutation({
    mutationFn: (id: string) => customerService.resetPassword(id),
    onSuccess: (res) => {
      void swalConfirm({
        title: 'Password reset',
        html: `<div style="text-align:left"><b>Username:</b> <code>${res.username ?? '—'}</code><br/><b>Temp Password:</b> <code>${res.temporaryPassword}</code><br/><small>Shown once — copy now.</small></div>`,
        confirmText: 'Done',
        showCancel: false,
      });
    },
    onError: () => toast.error('Failed to reset password'),
  });

  const handleDelete = async (c: Customer) => { if (await confirmDelete(c.name)) deleteM.mutate(c.id); };
  const handleReset = async (c: Customer) => {
    const ok = await swalConfirm({ title: `Reset password for ${c.name}?`, text: 'A new temporary password will be generated.', confirmText: 'Reset' });
    if (ok) resetM.mutate(c.id);
  };

  const openCreate = () => { setEditTarget(null); setModalOpen(true); };
  const openEdit = (c: Customer) => { setEditTarget(c); setModalOpen(true); };
  const handleSearch = (v: string) => { setSearch(v); setPage(1); };

  if (isLoading) return <SkeletonPage />;
  // Audit F-2 — a refusal is not an absence. Without this a 403 falls through
  // to the empty state, which reads as a fact about the tenant.
  if (isForbiddenError(error)) return <AccessRestricted />;

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Customers</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">Manage customer accounts, locations and contacts</p>
        </div>
        <div className="mt-3 flex gap-2 sm:mt-0">
          {canDelete && (
            <Button variant="secondary" onClick={() => navigate(ROUTES.CUSTOMERS_RECOVERY)} className="min-h-[44px]">
              <FontAwesomeIcon icon={faTrashArrowUp} className="mr-2 h-3.5 w-3.5" /> Recovery Bin
            </Button>
          )}
          {canCreate && (
            <Button onClick={openCreate} className="min-h-[44px]">
              <FontAwesomeIcon icon={faPlus} className="mr-2 h-3.5 w-3.5" /> Add Customer
            </Button>
          )}
        </div>
      </div>

      <SearchBar value={search} onChange={handleSearch} placeholder="Search by name, code, or email..." />

      {data && data.total > 0 ? (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[1100px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  {['Code', 'Name', 'Organization', 'GST', 'Username', 'Email', 'Phone', 'Status', 'Loc', 'Equip', 'Created'].map((h) => (
                    <th key={h} className="px-3 py-3 font-medium text-foreground">{h}</th>
                  ))}
                  <th className="px-3 py-3 text-right font-medium text-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((c) => (
                  <tr key={c.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                    <td className="px-3 py-3 font-mono text-xs text-foreground">{c.customerCode ?? '—'}</td>
                    <td className="px-3 py-3 font-medium text-foreground">
                      <button onClick={() => navigate(`/customers/${c.id}`)} className="hover:text-primary hover:underline">{c.name}</button>
                    </td>
                    <td className="px-3 py-3 text-muted-foreground">{c.organizationName ?? '—'}</td>
                    <td className="px-3 py-3 text-muted-foreground">{c.gstNumber ?? '—'}</td>
                    <td className="px-3 py-3 font-mono text-xs text-muted-foreground">{c.username ?? '—'}</td>
                    <td className="px-3 py-3 text-muted-foreground">{c.email ?? '—'}</td>
                    <td className="px-3 py-3 text-muted-foreground">{c.phone ?? '—'}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${c.isActive ? 'bg-emerald-500/10 text-emerald-600' : 'bg-muted text-muted-foreground'}`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center text-muted-foreground">{c.locationsCount ?? '—'}</td>
                    <td className="px-3 py-3 text-center text-muted-foreground">{c.equipmentCount ?? '—'}</td>
                    <td className="px-3 py-3 text-xs text-muted-foreground">{new Date(c.createdAt).toLocaleDateString()}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-0.5">
                        <IconBtn icon={faEye} label={`View ${c.name}`} onClick={() => navigate(`/customers/${c.id}`)} />
                        {canEdit && <IconBtn icon={faPencil} label={`Edit ${c.name}`} onClick={() => openEdit(c)} />}
                        {canEdit && <IconBtn icon={c.isActive ? faToggleOn : faToggleOff} label={c.isActive ? 'Disable' : 'Enable'} onClick={() => toggleM.mutate({ id: c.id, isActive: !c.isActive })} />}
                        {canReset && <IconBtn icon={faKey} label="Reset password" onClick={() => handleReset(c)} />}
                        {canDelete && <IconBtn icon={faTrash} label={`Delete ${c.name}`} onClick={() => handleDelete(c)} danger />}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPageChange={setPage} />
        </>
      ) : (
        <EmptyState
          icon={faUsers}
          title={search ? 'No customers match your search' : 'No customers yet'}
          description={search ? 'Try a different search term.' : 'Add your first customer to get started.'}
          action={!search && canCreate ? <Button onClick={openCreate}>Add Customer</Button> : undefined}
        />
      )}

      <CustomerFormModal
        open={modalOpen}
        editTarget={editTarget}
        onClose={() => { setModalOpen(false); setEditTarget(null); }}
        onSaved={invalidate}
      />
    </div>
  );
}
